<template>
    <div class="flex flex-col overflow-hidden max-w-screen-sm mx-auto w-full h-app max-h-app bg-gray-50">
        <!-- header -->
        <TitleBarCard
            :title="item.name"
            @close="close"
            :sharable="true"
            @share="share"
        />

        <!-- 產品明細 -->
        <main class="grow overflow-y-auto" data-view="body">
            <!-- 產品圖片及文字 (Edge-to-Edge, 不用卡片包覆) -->
            <div class="flex items-start space-x-4 px-5 py-5 bg-white border-b border-gray-100">
                <div class="shrink-0">
                    <div class="w-20 h-20 flex items-center justify-center overflow-hidden rounded-lg">
                        <img
                            :src="cdnImage(item.image || '/imgs/no_photo.jpg', 160)"
                            :alt="item.name"
                            width="80"
                            height="80"
                            loading="eager"
                            fetchpriority="high"
                            class="object-contain w-full h-full mix-blend-multiply"
                        />
                    </div>
                </div>

                <div class="grow pt-0.5">
                    <h1 class="font-bold text-gray-800 mb-1 leading-snug">{{ item.name }}</h1>
                    <template v-if="selected_product">
                        <div class="text-[13px] text-gray-500 mt-1 flex flex-col space-y-1">
                            <p class="line-clamp-2">
                                {{ selected_product.name }}
                            </p>
                        </div>
                    </template>
                </div>
            </div>

            <div class="leading-none tracking-wider pb-6">
                <!-- 選擇面額 -->
                <div class="bg-white mt-2" v-if="item.products.length">
                    <h2 class="text-[13px] font-bold text-gray-800 mb-0 block px-5 py-4 border-b border-gray-50">
                        選擇面值
                    </h2>
                    <ul class="flex flex-col">
                        <li
                            v-for="product in item.products"
                            :key="product.no"
                            class="relative transition-colors cursor-pointer border-b border-gray-50 flex items-center px-5 py-4 w-full"
                            :class="[
                                product.stockout
                                    ? 'opacity-50 cursor-not-allowed bg-gray-50/50'
                                    : product.no === form.product
                                        ? 'bg-primary/5'
                                        : 'bg-white hover:bg-gray-50'
                            ]"
                            @click="setForm(product)"
                        >
                            <div class="flex-grow pr-4">
                                <!-- Label/Name -->
                                <div class="text-[14px] leading-snug break-words" :class="product.no === form.product ? 'text-primary font-bold' : 'text-gray-800 font-medium'">
                                    {{ product.name }}
                                </div>
                            </div>

                            <!-- Right Side: Price -->
                            <div class="shrink-0 flex flex-col items-end">
                                <div class="text-primary flex items-baseline">
                                    <span class="text-[11px] font-bold" v-if="!product.stockout">{{ currency }}</span>
                                    <span class="text-[16px] font-bold tracking-tight ml-0.5" v-if="product.stockout">售罄</span>
                                    <span class="text-[18px] font-bold tracking-tight ml-[2px]" v-else>{{ product.discount || product.price | number_format }}</span>
                                </div>
                                <del class="text-gray-500 text-[11px] font-normal mt-0.5" v-if="product.discount && product.discount != product.price">
                                    {{ product.price | number_format }}
                                </del>
                            </div>

                            <!-- Radio indicator -->
                            <div
                                class="ml-4 w-[20px] h-[20px] rounded-full flex items-center justify-center shrink-0 transition-all border-2"
                                :class="product.no === form.product ? 'border-primary bg-primary text-white scale-110' : 'border-gray-200 bg-white'"
                            >
                                <svg
                                    v-if="product.no === form.product"
                                    xmlns="http://www.w3.org/2000/svg"
                                    class="h-[12px] w-[12px]"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                            </div>
                        </li>
                    </ul>
                </div>

                <!-- 填寫相關資料 -->
                <div
                    class="px-5 py-5 border-b border-gray-100 bg-white mt-2"
                    data-view="options"
                    v-if="selected_product && selected_product.fields?.length"
                >
                    <h2 class="text-[13px] font-bold text-gray-800 mb-4 block">
                        填寫資料
                    </h2>
                    <GameIdPicker
                        v-if="$auth.loggedIn"
                        :category-id="item.id"
                        @apply="applyGameId"
                    />
                    <div
                        v-for="option in selected_product.fields"
                        :key="option.name"
                        class="mb-5 last:mb-0"
                    >
                        <label
                            class="block text-[13px] font-bold text-gray-700 mb-2"
                            :for="option.name"
                            v-text="
                                option.label_zh != ''
                                    ? option.label_zh
                                    : option.label
                            "
                        />
                        <OptionInput
                            v-model="form.options[option.name]"
                            :option="option"
                        />
                    </div>
                </div>

                <!-- 商品介紹 -->
                <div class="px-5 py-6 bg-white mt-2" v-if="item.description">
                    <h2 class="text-[13px] font-bold text-gray-800 mb-3 block">
                        商品介紹
                    </h2>
                    <div class="text-[13px] text-gray-600 leading-relaxed max-w-full overflow-hidden break-words content-reader">
                        <HtmlReader :text="item.description" />
                    </div>
                </div>

                <!-- 商品評價 -->
                <ReviewList :category-id="item.id" />
            </div>
        </main>
        
        <!-- 底部區 (Edge-to-Edge Flat) -->
        <div class="shrink border-t border-gray-100 bg-white px-5 pt-3 pb-safe z-50">
            <template v-if="selected_product">
                <div class="flex justify-between items-center mb-3">
                    <div class="flex items-baseline space-x-1">
                        <span class="text-[12px] text-gray-500 font-medium tracking-wide">總計</span>
                        <div class="text-primary flex items-baseline border-b-2 border-primary/20 pb-0.5">
                            <span class="text-sm font-bold">{{ currency }}</span>
                            <span class="text-2xl font-black tracking-tight ml-0.5">{{ selected_product.discount || selected_product.price | number_format }}</span>
                        </div>
                    </div>
                    <QuantityPanel
                        v-if="!stockout"
                        :disabled="submitting"
                        v-model="form.quantity"
                        :onlyonce="forOnce"
                    />
                </div>
                <!-- 底部預留避免異形屏高度被擋 -->
                <div class="pb-3">
                    <button
                        type="button"
                        class="btn w-full h-[52px] rounded-xl font-bold tracking-wider shadow-sm hover:shadow-md transition-all flex items-center justify-center"
                        @click="submit"
                        :disabled="stockout || submitting"
                    >
                        <span
                            v-if="submitting"
                            class="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2 inline-block align-middle"
                        ></span>
                        加入購物車
                    </button>
                </div>
            </template>
            <!-- 所有商品售罄 -->
            <div v-else class="text-center text-gray-500 py-4 pb-6">
                <p class="font-bold">已售罄</p>
                <p class="text-xs mt-1">目前暫無可供選購庫存</p>
            </div>
        </div>
    </div>
</template>

<script>
import HtmlReader from "@luminexs/components/form/HTMLReader.vue";
import SpecificationItem from "@/components/order/SpecificationItem";
import QuantityPanel from "@/components/order/QuantityPanel";
import OptionInput from "@/components/product/specification/OptionInput";
import GameIdPicker from "@/components/product/GameIdPicker.vue";
import TitleBarCard from "@/components/card/TitleBarCard.vue";
import ReviewList from "@/components/review/ReviewList.vue";
import { mapActions, mapGetters } from "vuex";
import { first } from "lodash";

export default {
    components: {
        SpecificationItem,
        QuantityPanel,
        OptionInput,
        GameIdPicker,
        HtmlReader,
        TitleBarCard,
        ReviewList,
    },

    props: {
        height: {
            type: String,
            default: "h-app",
        },
        item: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            form: {},
            selected_product: null,
            external: 0,
            submitting: false,
            hasTrackedViewItem: false,
            sharedata: {
                title: "",
                text: "",
                url: "",
            },
        };
    },
    methods: {
        ...mapActions({
            fetch: "cart/fetch",
        }),

        close() {
            this.$parent.close();
        },

        async share() {
            this.$share.open({
                title: `${this.info.name} - ${this.item.name}`,
                text: this.item.description,
                url: `${window.location.origin}${this.localePath(
                    `/order/product/${this.item.no}`
                )}`,
            });
        },

        setForm(_product) {
            const product =
                _product ||
                first(this.item.products.filter((p) => !p.stockout));

            if (!product || product.stockout) return;

            this.selected_product = product;

            let form_options = {};
            product.fields.forEach((option) => {
                form_options[option.name] = "";
            });

            this.form = {
                product: product.no,
                options: form_options,
                quantity: 1,
            };
            if (!_product) return;
            this.$nextTick(() => {
                const el = this.$el.querySelector('[data-view="options"]');
                if (el) {
                    this.$el
                        .querySelector('[data-view="body"]')
                        .scrollTo({
                            top: el.offsetTop - 250,
                            behavior: "smooth",
                        });
                }
            });
        },

        async submit() {
            if (this.submitting || this.stockout) return;
            if (
                this.isGuest(this.localePath(`/order/product/${this.item.no}`))
            ) {
                this.close();
                return;
            }
            try {
                this.submitting = true;
                await this.$axios.post("/api/cart", this.form);
                if (this.$track) {
                    this.$track.addToCart(
                        this.item,
                        this.selected_product,
                        this.form.quantity
                    );
                }
                this.fetch();
                this.close();
                this.$loader.success("已加入購物車");
            } catch (e) {
                this.handleErrorsMessage(e);
            } finally {
                this.submitting = false;
            }
        },
        applyGameId(options) {
            if (!this.selected_product || !options) return;

            const nextOptions = { ...this.form.options };

            this.selected_product.fields.forEach((field) => {
                if (Object.prototype.hasOwnProperty.call(options, field.name)) {
                    nextOptions[field.name] = options[field.name];
                }
            });

            this.$set(this.form, "options", nextOptions);
            this.$loader.success("已套用遊戲ID");
        },
        trackViewItem() {
            if (!this.$track || this.hasTrackedViewItem || !this.selected_product) return;

            this.hasTrackedViewItem = true;
            this.$track.viewItem(this.item, this.selected_product);
        },
    },

    computed: {
        ...mapGetters({
            info: "app/info",
            isSafari: "app/isSafari",
        }),
        show() {
            return this.$parent.show;
        },
        price() {
            const { discount, price } = this.selected_product;
            return discount || price;
        },
        total() {
            return this.price * this.form.quantity;
        },
        stockout() {
            if (!this.selected_product) return true;
            return this.selected_product.stockout;
        },
        forOnce() {
            if (!this.selected_product) return true;
            return this.selected_product.once_for_each_only;
        },
    },

    created() {
        this.setForm();
    },
    mounted() {
        this.trackViewItem();
    },
};
</script>

<style lang="css" scoped>
.browser {
    height: 100vh;
}

.safari {
    height: calc(100vh - 75px);
}
.pb-safe {
    padding-bottom: env(safe-area-inset-bottom, 16px);
}
</style>
